import { workApprovalMode } from '@/constants/workApproval';
/**
 * Coworker threads store.
 * Shape mirrors useInferenceTasksStore conventions: map by id + ordered ids,
 * per-op loading/error flags. Threads RENDER through the conversation UI
 * (docs/COWORKER_CONVERSATION_UNIFICATION.md): live updates are driven by
 * hooks/useCoworkerChat (SSE/poll), so no per-task event cursors live here —
 * only task records, composer mode, and schedule CRUD.
 */
import { create } from 'zustand';
import { Platform } from 'react-native';
import { prepareLocalProjectLaunch, releaseLocalProjectLaunch, withLocalProjectHandoff } from '@/lib/localProject';
import { prepareLocalDesktopLaunch, releaseLocalDesktopLaunch } from '@/lib/localDesktop';
import { preparePluginLaunch } from '@/lib/plugins/pluginStore';
import { coworkerApi } from '@/lib/api';
import {
  isFatWorkEvent,
  isLiveWorkDelta,
  slimWorkEvent,
} from '@/constants/work';
import { isWorkTranscriptEvent, mergeWorkTranscriptEvents, workPendingSendIsSettled } from '@/lib/workTranscript';
import {
  DEFAULT_WORK_THINKING_POWER,
  thinkingPowerById,
} from '@/constants/workThinkingPower';
import {
  createWorkFollowUp,
  enqueueWorkFollowUp,
  normalizeWorkFollowUpQueue,
  removeWorkFollowUp,
  updateWorkFollowUp,
  reorderWorkFollowUp,
} from '@/constants/work_followup_queue';

const FOLLOWUP_STORAGE_KEY = 'gab.workFollowUpQueue.v1';
// v2: the default moved from Fast to Standard. Bumping the key drops stored
// v1 values, which were almost all the old default rather than a real choice.
const THINKING_POWER_KEY = 'gab.workThinkingPower.v2';

export function taskIdFromChat(chat) {
  const raw = chat?.coworkerSession;
  if (!raw) return null;
  if (typeof raw === 'object') return String(raw._id || raw.id || '') || null;
  return String(raw);
}

const taskIdsByConversation = new WeakMap();

/** Resume/steer need a task id even when the chat stub omitted coworkerSession. */
export function findTaskIdForConversation(conversationId, { chat, tasks = {}, taskIds = [] } = {}) {
  const fromChat = taskIdFromChat(chat);
  if (fromChat) return fromChat;
  if (!conversationId || !taskIds.length) return null;
  // Each mounted message resolves its bot through this selector. Token draft
  // updates keep both collections unchanged, so share their ordered lookup.
  let byOrder = taskIdsByConversation.get(tasks);
  if (!byOrder) {
    byOrder = new WeakMap();
    taskIdsByConversation.set(tasks, byOrder);
  }
  let byConversation = byOrder.get(taskIds);
  if (!byConversation) {
    byConversation = new Map();
    for (const id of taskIds) {
      const task = tasks[id];
      const linked = task?.conversationId || task?.conversation;
      if (linked && !byConversation.has(String(linked))) {
        byConversation.set(String(linked), String(task.id || id));
      }
    }
    byOrder.set(taskIds, byConversation);
  }
  return byConversation.get(String(conversationId)) || null;
}

function canUseSessionStorage() {
  try {
    return typeof sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function readFollowUpQueues() {
  if (!canUseSessionStorage()) return {};
  try {
    const raw = sessionStorage.getItem(FOLLOWUP_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    for (const [taskId, list] of Object.entries(parsed)) {
      if (!taskId) continue;
      const queue = normalizeWorkFollowUpQueue(list);
      if (queue.length) out[taskId] = queue;
    }
    return out;
  } catch {
    return {};
  }
}

function writeFollowUpQueues(map) {
  if (!canUseSessionStorage()) return;
  try {
    sessionStorage.setItem(FOLLOWUP_STORAGE_KEY, JSON.stringify(map || {}));
  } catch { /* quota / private mode — in-memory queue still works */ }
}

function persistFollowUpQueues(state) {
  writeFollowUpQueues(state.followUpQueueByTask);
}

function readThinkingPower() {
  if (!canUseSessionStorage()) return DEFAULT_WORK_THINKING_POWER;
  try {
    const raw = localStorage.getItem(THINKING_POWER_KEY);
    return thinkingPowerById(raw).id;
  } catch {
    return DEFAULT_WORK_THINKING_POWER;
  }
}

function writeThinkingPower(id) {
  if (!canUseSessionStorage()) return;
  try {
    localStorage.setItem(THINKING_POWER_KEY, thinkingPowerById(id).id);
  } catch { /* quota / private mode */ }
}

function isPlaceholderWorkChatTitle(title) {
  if (!title || typeof title !== 'string') return true;
  const normalized = title.trim().toLowerCase();
  return normalized === 'untitled chat'
    || normalized === 'new chat'
    || normalized === 'untitled conversation'
    || normalized === 'untitled work'
    || normalized === 'coworker thread';
}

// Same kick as createConversation: seed the sidebar row, then ask the
// backend to name the thread. Lazy-require the chat store to avoid a cycle
// (store/index.js re-exports this module before useChatStore exists).
function kickOffWorkChatTitle(created, collectionId) {
  if (!created?.conversationId) return;
  try {
    const { useAuthStore, useChatStore } = require('@/store');
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;
    const chatStore = useChatStore.getState();
    if (!chatStore.chats.some((c) => c.id === created.conversationId)) {
      chatStore.addChats([{
        id: created.conversationId,
        title: created.title || 'Untitled Chat',
        user: userId,
        flags: { isCoworkerSession: true },
        coworkerSession: created.id,
        hasMacProject: !!created.localProject?.bindingId,
        messages: [],
        ...(collectionId ? { inCollectionId: collectionId } : {}),
      }]);
    } else if (collectionId) {
      chatStore.updateLocalChatDetails(created.conversationId, { inCollectionId: collectionId });
    }
    if (isPlaceholderWorkChatTitle(created.title)) {
      chatStore.retryTitleRetrieval(userId, created.conversationId);
    }
  } catch (_) { /* title gen is best-effort */ }
}

// The server materializes the first user bubble only after the execution
// host accepts the launch. Show the prompt right away as a pending send; it
// settles automatically once the matching user_message event arrives.
function seedLaunchPromptRow(state, created, params) {
  const taskId = created?.id ? String(created.id) : null;
  const text = String(params?.prompt || '').trim();
  if (!taskId || !text || created?.adopted) return;
  let files = [];
  try {
    const { useFileStore } = require('@/store');
    const byId = useFileStore.getState().files || {};
    files = (params?.fileIds || []).flatMap((id) => (byId[id] ? [byId[id]] : []));
  } catch (_) { /* attachments render once the server row lands */ }
  state.addPendingSend(taskId, {
    id: `launch-${taskId}`,
    text,
    files,
    createdAt: Date.now(),
    afterSeq: 0,
  });
}

function linkWorkChatToCollection(created, collectionId) {
  kickOffWorkChatTitle(created, collectionId);
  if (collectionId && created?.conversationId) {
    try {
      const { useCollectionStore } = require('@/store');
      useCollectionStore.getState().addConversationToCollectionLocally(
        collectionId,
        created.conversationId,
      );
    } catch (_) { /* collection membership is best-effort */ }
  }
}

const MAX_EVENTS_PER_TASK = 500;
const checkpointFetches = new Map();

// Seen seqs per task, kept beside the store rather than in it. Rebuilding a
// Set from the whole log on every SSE frame was O(events) per frame; with
// the index, a frame of N new events costs O(N). Seqs that fall off the
// MAX_EVENTS_PER_TASK cap stay in the index — a re-delivered old seq must
// still be dropped, never appended out of order.
const seenSeqByTask = new Map();

function seqIndexFor(taskId, events) {
  let index = seenSeqByTask.get(taskId);
  if (index) return index;
  index = new Set();
  for (const e of events) index.add(e.seq);
  seenSeqByTask.set(taskId, index);
  return index;
}

// Task list payloads arrive every minute from CoworkerWatcher. Handing every
// subscriber a fresh object for a task nothing changed on re-derived the
// live projection in every open Work thread for no reason.
function shallowEqualTask(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    const va = a[key];
    const vb = b[key];
    if (va === vb) continue;
    // Nested budget / artifacts records are rebuilt per payload; compare by
    // value so an unchanged task keeps its identity.
    if (va && vb && typeof va === 'object' && typeof vb === 'object') {
      if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
      continue;
    }
    return false;
  }
  return true;
}

export const useCoworkerStore = create((set, get) => ({
  tasks: {},
  taskIds: [],
  isLoading: false,
  error: null,

  // Execution event log per task, deduped by seq and kept in the store (not
  // in useCoworkerChat) so the transcript rows and the Work side rail read
  // the same stream the hook is driving.
  eventsByTask: {},
  // Plans can be published hundreds of events before a turn settles. Keep the
  // latest revision outside the capped log so an idle reload still has it
  // after the full snapshot is reduced to MAX_EVENTS_PER_TASK.
  latestPlanByTask: {},
  latestPlanEventSeqByTask: {},

  // Task ids currently followed by an in-thread useCoworkerChat SSE/poll.
  // CoworkerWatcher skips these so a viewed live thread is not opened twice.
  followingByTask: {},
  beginTaskFollow: (taskId) => {
    if (!taskId) return;
    set((state) => ({
      followingByTask: {
        ...state.followingByTask,
        [taskId]: (state.followingByTask[taskId] || 0) + 1,
      },
    }));
  },
  endTaskFollow: (taskId) => {
    if (!taskId) return;
    set((state) => {
      const nextCount = (state.followingByTask[taskId] || 1) - 1;
      if (nextCount <= 0) {
        const { [taskId]: _dropped, ...rest } = state.followingByTask;
        return { followingByTask: rest };
      }
      return {
        followingByTask: { ...state.followingByTask, [taskId]: nextCount },
      };
    });
  },

  // Task whose cloud browser is being watched/controlled. Held here so the
  // transcript card, the Work rail, and the composer can all open the same
  // full-screen viewer.
  browserViewerTaskId: null,
  browserViewerWatch: false,
  openBrowserViewer: (taskId, { watch = false } = {}) => set({ browserViewerTaskId: taskId || null, browserViewerWatch: watch }),
  closeBrowserViewer: () => set({ browserViewerTaskId: null }),

  // Transcript-relevant events (user turns, assistant replies, questions,
  // artifacts, lifecycle) with full text and no cap. The chat rows for a
  // Work thread are projected from these (lib/workTranscript.js); the
  // capped narration log above is for the activity trail only.
  transcriptEventsByTask: {},
  // The assistant reply being streamed right now, plus how much of it the
  // pacer has revealed. Owned by lib/workAssistantStream.js.
  liveDraftByTask: {},
  // Composer sends whose `user_message` event has not arrived yet.
  pendingSendsByTask: {},

  setLiveDraft: (taskId, draft) => {
    if (!taskId) return;
    set((state) => {
      if (state.liveDraftByTask[taskId] === draft) return state;
      if (!draft) {
        if (!(taskId in state.liveDraftByTask)) return state;
        const { [taskId]: _dropped, ...rest } = state.liveDraftByTask;
        return { liveDraftByTask: rest };
      }
      return { liveDraftByTask: { ...state.liveDraftByTask, [taskId]: draft } };
    });
  },

  addPendingSend: (taskId, send) => {
    if (!taskId || !send?.id) return;
    set((state) => {
      // The stream can acknowledge a launch before its create request returns.
      if (workPendingSendIsSettled(taskId, send, { events: state.transcriptEventsByTask[taskId] })) return state;
      return { pendingSendsByTask: {
        ...state.pendingSendsByTask,
        [taskId]: [...(state.pendingSendsByTask[taskId] || []), send],
      } };
    });
  },
  removePendingSend: (taskId, sendId) => {
    if (!taskId || !sendId) return;
    set((state) => {
      const current = state.pendingSendsByTask[taskId];
      if (!current?.some((s) => s.id === sendId)) return state;
      const rest = current.filter((s) => s.id !== sendId);
      const next = { ...state.pendingSendsByTask };
      if (rest.length) next[taskId] = rest;
      else delete next[taskId];
      return { pendingSendsByTask: next };
    });
  },
  // Acknowledgements can arrive through either events or conversation detail.
  // Retire them in the store, not just while the matching row is visible.
  settlePendingSends: (taskId, userEvents = [], messages = []) => {
    if (!taskId) return;
    set((state) => {
      const current = state.pendingSendsByTask[taskId];
      if (!current?.length) return state;
      const rest = current.filter((send) => !workPendingSendIsSettled(taskId, send, { messages, events: userEvents }));
      if (rest.length === current.length) return state;
      const next = { ...state.pendingSendsByTask };
      if (rest.length) next[taskId] = rest;
      else delete next[taskId];
      return { pendingSendsByTask: next };
    });
  },

  ingestTranscriptEvents: (taskId, incoming) => {
    if (!taskId || !Array.isArray(incoming) || incoming.length === 0) return;
    set((state) => {
      const prev = state.transcriptEventsByTask[taskId] || [];
      const next = mergeWorkTranscriptEvents(prev, incoming);
      if (next === prev) return state;
      return { transcriptEventsByTask: { ...state.transcriptEventsByTask, [taskId]: next } };
    });
    if (incoming.some((ev) => ev?.type === 'user_message')) {
      get().settlePendingSends(taskId, incoming);
    }
  },

  ingestTaskEvents: (taskId, incoming) => {
    if (!taskId || !Array.isArray(incoming) || incoming.length === 0) return;
    if (incoming.some(isWorkTranscriptEvent)) get().ingestTranscriptEvents(taskId, incoming);
    set((state) => {
      const prev = state.eventsByTask[taskId] || [];
      // Everything lands slim through this path; the remap only runs for a
      // log written by an older build that is still in memory.
      let base = prev;
      if (prev.length && (isFatWorkEvent(prev[prev.length - 1]) || isLiveWorkDelta(prev[prev.length - 1]))) {
        const remapped = prev.some(isFatWorkEvent) ? prev.map(slimWorkEvent) : prev;
        base = remapped.some(isLiveWorkDelta)
          ? remapped.filter((e) => !isLiveWorkDelta(e))
          : remapped;
      }
      const seen = seqIndexFor(taskId, base);
      const fresh = [];
      for (const e of incoming) {
        if (!e || !(Number(e.seq) > 0) || isLiveWorkDelta(e) || seen.has(e.seq)) continue;
        seen.add(e.seq);
        fresh.push(slimWorkEvent(e));
      }
      let task = state.tasks[taskId];
      for (const event of fresh) {
        if (task && event.type === 'goal_updated' && Number(event.seq) > (task.goalEventSeq || 0)) {
          task = { ...task, goal: event.goal || null, goalEventSeq: Number(event.seq) };
        }
        if (task && event.type === 'action_approval_changed' && Number(event.seq) > (task.actionApprovalEventSeq || 0)) {
          task = { ...task, actionApproval: workApprovalMode(event.actionApproval).id, actionApprovalEventSeq: Number(event.seq) };
        }
        const hasActiveTurn = Object.hasOwn(event, 'activeTurnId');
        // A late command receipt belongs to its original turn; it must not
        // replace the current control target merely because its seq is newer.
        if (task && (hasActiveTurn || (event.turnId && event.type !== 'project_command')) && Number(event.seq) > (task.turnEventSeq || 0)) {
          task = { ...task, activeTurnId: event.type === 'session_end' ? null : hasActiveTurn ? event.activeTurnId : event.turnId, turnEventSeq: Number(event.seq) };
        }
      }
      let latestPlan = state.latestPlanByTask[taskId] || null;
      let latestPlanSeq = state.latestPlanEventSeqByTask[taskId] || 0;
      for (const event of incoming) {
        if ((event?.type === 'plan_updated'
          || (event?.type === 'user_question_requested' && event.kind === 'plan'))
          && event.plan && Number(event.seq) > latestPlanSeq) {
          latestPlan = slimWorkEvent(event).plan || latestPlan;
          latestPlanSeq = Number(event.seq);
        }
      }
      const hasNewPlan = latestPlan !== (state.latestPlanByTask[taskId] || null);
      if (!fresh.length && base === prev && !hasNewPlan) return state;
      if (!fresh.length && base !== prev) {
        return {
          eventsByTask: { ...state.eventsByTask, [taskId]: base },
          ...(hasNewPlan ? {
            latestPlanByTask: { ...state.latestPlanByTask, [taskId]: latestPlan },
            latestPlanEventSeqByTask: { ...state.latestPlanEventSeqByTask, [taskId]: latestPlanSeq },
          } : {}),
        };
      }
      const merged = [...base, ...fresh];
      // A history snapshot may arrive after sidebar status or a newer live
      // event. Restore sequence order before trimming or building turns.
      if (fresh.length && base.length && fresh.some(event => Number(event.seq) < Number(base[base.length - 1].seq))) {
        merged.sort((a, b) => Number(a.seq) - Number(b.seq));
      }
      return {
        ...(task && task !== state.tasks[taskId] ? { tasks: { ...state.tasks, [taskId]: task } } : {}),
        eventsByTask: {
          ...state.eventsByTask,
          [taskId]: merged.slice(-MAX_EVENTS_PER_TASK),
        },
        ...(hasNewPlan ? {
          latestPlanByTask: { ...state.latestPlanByTask, [taskId]: latestPlan },
          latestPlanEventSeqByTask: { ...state.latestPlanEventSeqByTask, [taskId]: latestPlanSeq },
        } : {}),
      };
    });
  },

  // Chat ⇄ Work mode toggle for new-chat composers (home, collections).
  // When 'coworker', send routes the composed text into a new Work thread
  // instead of a chat conversation.
  composerMode: 'chat',
  setComposerMode: (mode) => set({ composerMode: mode === 'coworker' ? 'coworker' : 'chat' }),
  composerBudgetTokens: 200000,
  composerIncludeMemory: true,
  composerActionApproval: 'smart',
  setComposerActionApproval: value => set({ composerActionApproval: workApprovalMode(value).id }),
  composerAutoApprove: false,
  setComposerAutoApprove: (value) => set({ composerAutoApprove: value === true }),
  composerThinkingPower: readThinkingPower(),
  composerOrchestrator: { orchestratorModel: '', reasoningEffort: '' },
  setComposerOrchestrator: settings => set({ composerOrchestrator: settings }),
  setComposerBudgetTokens: (value) => set({ composerBudgetTokens: Number(value) || 200000 }),
  setComposerIncludeMemory: (value) => set({ composerIncludeMemory: !!value }),
  setComposerThinkingPower: (value) => {
    const id = thinkingPowerById(value).id;
    writeThinkingPower(id);
    set({ composerThinkingPower: id });
  },
  getComposerLaunchOptions: () => {
    const state = get();
    return {
      budgetTokens: state.composerBudgetTokens,
      includeMemory: state.composerIncludeMemory,
      autoApprove: state.composerAutoApprove,
      actionApproval: state.composerActionApproval,
      thinkingPower: thinkingPowerById(state.composerThinkingPower).id,
      ...(['macos', 'web'].includes(Platform.OS) ? state.composerOrchestrator : {}),
    };
  },

  // Server-backed follow-ups typed while a run is live. This map is an
  // optimistic projection and sessionStorage is only a migration/offline
  // cache; task detail always replaces it with the durable queue.
  followUpQueueByTask: readFollowUpQueues(),
  setFollowUpQueue: (taskId, queue) => set((state) => {
    const normalized = normalizeWorkFollowUpQueue(queue);
    const next = { ...state.followUpQueueByTask };
    if (normalized.length) next[taskId] = normalized;
    else delete next[taskId];
    persistFollowUpQueues({ followUpQueueByTask: next });
    return { followUpQueueByTask: next };
  }),
  enqueueFollowUp: (taskId, item, options) => {
    if (!taskId) return null;
    const queued = item?.id ? item : createWorkFollowUp(item);
    if (!queued) return null;
    const current = get().followUpQueueByTask[taskId] || [];
    const nextQueue = enqueueWorkFollowUp(current, queued, options);
    const next = { ...get().followUpQueueByTask, [taskId]: nextQueue };
    persistFollowUpQueues({ followUpQueueByTask: next });
    set({ followUpQueueByTask: next });
    require('@/lib/remoteWork').withRemoteWorkTask(get().tasks[taskId] || { id: taskId }, guard =>
      coworkerApi.enqueueFollowUp(taskId, { ...queued, front: options?.front === true }, guard))
      .then((queue) => get().setFollowUpQueue(taskId, queue))
      .catch((err) => {
        set({ error: err?.message || 'Failed to queue follow-up' });
        void get().fetchTask(taskId);
      });
    return queued;
  },
  updateFollowUp: (taskId, id, patch) => {
    if (!taskId || !id) return;
    set((state) => {
      const current = state.followUpQueueByTask[taskId];
      if (!current) return state;
      const nextQueue = updateWorkFollowUp(current, id, patch);
      const next = { ...state.followUpQueueByTask, [taskId]: nextQueue };
      persistFollowUpQueues({ followUpQueueByTask: next });
      return { followUpQueueByTask: next };
    });
    require('@/lib/remoteWork').withRemoteWorkTask(get().tasks[taskId] || { id: taskId }, guard => coworkerApi.updateFollowUp(taskId, id, patch, guard))
      .then((queue) => get().setFollowUpQueue(taskId, queue))
      .catch(() => { void get().fetchTask(taskId); });
  },
  removeFollowUp: (taskId, id) => {
    if (!taskId || !id) return;
    set((state) => {
      const current = state.followUpQueueByTask[taskId];
      if (!current) return state;
      const nextQueue = removeWorkFollowUp(current, id);
      const next = { ...state.followUpQueueByTask };
      if (nextQueue.length) next[taskId] = nextQueue;
      else delete next[taskId];
      persistFollowUpQueues({ followUpQueueByTask: next });
      return { followUpQueueByTask: next };
    });
    coworkerApi.removeFollowUp(taskId, id)
      .then((queue) => get().setFollowUpQueue(taskId, queue))
      .catch(() => { void get().fetchTask(taskId); });
  },
  reorderFollowUp: (taskId, fromIndex, toIndex) => {
    if (!taskId) return;
    let orderedIds = null;
    set((state) => {
      const current = state.followUpQueueByTask[taskId];
      if (!current?.length) return state;
      const from = Number(fromIndex);
      const to = Number(toIndex);
      if (from === to) return state;
      const nextQueue = reorderWorkFollowUp(current, from, to);
      orderedIds = nextQueue.map((entry) => entry.id);
      const next = { ...state.followUpQueueByTask, [taskId]: nextQueue };
      persistFollowUpQueues({ followUpQueueByTask: next });
      return { followUpQueueByTask: next };
    });
    if (orderedIds) coworkerApi.reorderFollowUps(taskId, orderedIds)
      .then((queue) => get().setFollowUpQueue(taskId, queue))
      .catch(() => { void get().fetchTask(taskId); });
  },
  clearFollowUps: (taskId) => {
    if (!taskId) return;
    set((state) => {
      if (!state.followUpQueueByTask[taskId]) return state;
      const next = { ...state.followUpQueueByTask };
      delete next[taskId];
      persistFollowUpQueues({ followUpQueueByTask: next });
      return { followUpQueueByTask: next };
    });
    coworkerApi.clearFollowUps(taskId)
      .then((queue) => get().setFollowUpQueue(taskId, queue))
      .catch(() => { void get().fetchTask(taskId); });
  },

  reset: () => {
    seenSeqByTask.clear();
    set({
      tasks: {},
      taskIds: [],
      eventsByTask: {},
      transcriptEventsByTask: {},
      liveDraftByTask: {},
      pendingSendsByTask: {},
      latestPlanByTask: {},
      latestPlanEventSeqByTask: {},
      checkpointsByTask: {},
      followingByTask: {},
      followUpQueueByTask: {},
      handoffReviewByTask: {},
      isLoading: false,
      error: null,
      schedules: [],
      schedulesLoading: false,
      schedulesError: null,
      composerMode: 'chat',
      composerAutoApprove: false,
      composerActionApproval: 'smart',
    });
  },

  addTask: (task) => set((state) => {
    if (!task?.id) return state;
    const prev = state.tasks[task.id];
    const next = { ...prev, ...task };
    if (prev && Object.hasOwn(task, 'goal') && (task.goalEventSeq || 0) < (prev.goalEventSeq || 0)) {
      next.goal = prev.goal;
      next.goalEventSeq = prev.goalEventSeq;
    }
    if (prev?.budget || next.budget) {
      next.budget = { ...prev?.budget, ...task.budget };
    }
    const sameTask = shallowEqualTask(prev, next);
    const known = state.taskIds.includes(task.id);
    if (sameTask && known) return state;
    return {
      tasks: sameTask ? state.tasks : { ...state.tasks, [task.id]: next },
      taskIds: known ? state.taskIds : [task.id, ...state.taskIds],
    };
  }),

  fetchTasks: async () => {
    set({ isLoading: true, error: null });
    try {
      const tasks = await coworkerApi.fetchTasks();
      set((state) => {
        const map = { ...state.tasks };
        const ids = [];
        let changed = false;
        for (const t of tasks) {
          const id = String(t.id || t._id);
          const prev = map[id];
          const incoming = { ...t, id };
          const next = { ...prev, ...incoming };
          // List payloads omit billed credits; keep a prior detail fetch.
          if (incoming.creditsUsed == null && prev?.creditsUsed != null) {
            next.creditsUsed = prev.creditsUsed;
          }
          if (prev?.budget) {
            next.budget = { ...prev.budget, ...incoming.budget };
          }
          if (shallowEqualTask(prev, next)) {
            map[id] = prev;
          } else {
            map[id] = next;
            changed = true;
          }
          ids.push(id);
        }
        const sameIds = ids.length === state.taskIds.length
          && ids.every((id, i) => id === state.taskIds[i]);
        return {
          tasks: changed ? map : state.tasks,
          taskIds: sameIds ? state.taskIds : ids,
          isLoading: false,
        };
      });
      return tasks;
    } catch (err) {
      set({ isLoading: false, error: err?.message || 'Failed to load tasks' });
      return null;
    }
  },

  fetchTask: async (taskId) => {
    try {
      const raw = await coworkerApi.fetchTask(taskId);
      if (!raw) return null;
      const task = { ...raw, id: String(raw.id || raw._id || taskId) };
      get().addTask(task);
      get().setFollowUpQueue(task.id, raw.followUpQueue || []);
      return task;
    } catch (err) {
      set({ error: err?.message || 'Failed to load task' });
      return null;
    }
  },

  createTask: async (params, options = {}) => {
    const remote = await require('@/lib/remoteWork').createRemoteWorkTask(params, options);
    if (remote) {
      set({ composerAutoApprove: false, composerActionApproval: 'smart' });
      get().addTask(remote);
      linkWorkChatToCollection(remote, params?.collectionId || remote?.collectionId);
      seedLaunchPromptRow(get(), remote, params);
      return remote;
    }
    const binding = await prepareLocalProjectLaunch({ collectionId: params?.collectionId });
    let desktop;
    let created;
    try {
      desktop = await prepareLocalDesktopLaunch();
      const plugins = await preparePluginLaunch(desktop);
      const guards = [options.isRequestCurrent, binding?.isCurrent, desktop?.isCurrent, plugins?.isCurrent].filter(Boolean);
      const isCurrent = guards.length === 1 ? guards[0] : guards.length ? () => guards.every(guard => guard()) : undefined;
      if (isCurrent && !isCurrent()) throw new Error('Your account changed. Send the task again.');
      const launchParams = { ...params, ...(plugins && { installedPluginIds: plugins.installedPluginIds }),
        ...(binding && { localProject: binding.project }), ...(desktop && { localDesktop: desktop.localDesktop }) };
      created = isCurrent ? await coworkerApi.createTask(launchParams, { isRequestCurrent: isCurrent })
        : await coworkerApi.createTask(launchParams);
      if (isCurrent && !isCurrent()) throw new Error('Your account changed. Send the task again.');
      if (binding) created = { ...created, localProject: binding.project };
      if (desktop) created = { ...created, localDesktop: desktop.localDesktop };
    } catch (error) {
      await releaseLocalProjectLaunch(binding);
      await releaseLocalDesktopLaunch(desktop);
      throw error;
    }
    set({ composerAutoApprove: false, composerActionApproval: 'smart' });
    get().addTask(created);
    linkWorkChatToCollection(created, params?.collectionId || created?.collectionId);
    seedLaunchPromptRow(get(), created, params);
    return created;
  },

  handoffReviewByTask: {},
  setHandoffReview: (taskId, review) => set(state => ({ handoffReviewByTask: { ...state.handoffReviewByTask, [taskId]: review } })),
  registerHandoffDestination: (created) => {
    get().addTask(created);
    linkWorkChatToCollection(created, created?.collectionId);
  },

  steerTask: async (taskId, message, fileIds, options) => {
    options = { ...options, expectedTurnId: get().tasks[taskId]?.activeTurnId || null };
    return require('@/lib/remoteWork').withRemoteWorkTask(get().tasks[taskId] || { id: taskId }, remoteGuard =>
      withLocalProjectHandoff((extra, guard) => coworkerApi.steerTask(taskId, message, fileIds, { ...options, ...extra }, { ...guard, ...remoteGuard })));
  },

  setTaskActionApproval: async (taskId, actionApproval) => {
    const out = await coworkerApi.setTaskActionApproval(taskId, actionApproval);
    get().addTask({ id: taskId, actionApproval: out.actionApproval });
    return out;
  },

  setTaskAutoApprove: async (taskId, autoApprove) => {
    const out = await coworkerApi.setTaskAutoApprove(taskId, autoApprove);
    get().addTask({ id: taskId, autoApprove: out.autoApprove });
    return out;
  },

  setTaskOrchestrator: async (taskId, settings) => {
    const out = await coworkerApi.setTaskOrchestrator(taskId, settings);
    get().addTask({ id: taskId, orchestratorModel: out.orchestratorModel, reasoningEffort: out.reasoningEffort });
    return out;
  },

  setTaskModel: async (taskId, thinkingPower) => {
    const out = await coworkerApi.setTaskThinkingPower(taskId, thinkingPower);
    if (taskId && out?.thinkingPower) {
      const current = get().tasks[taskId];
      get().addTask({ id: taskId, ...(current || {}), thinkingPower: out.thinkingPower });
    }
    return out;
  },

  stopTask: async (taskId) => {
    const out = await coworkerApi.stopTask(taskId, get().tasks[taskId]?.activeTurnId);
    const current = get().tasks[taskId];
    if (current) get().addTask({ id: taskId, status: out.status || current.status,
      cancellationPending: out.cancellationPending === true });
    return out;
  },

  resumeTask: async (taskId, message, fileIds, options) => {
    const current = get().tasks[taskId];
    if (current?.status === 'running') options = { ...options, expectedTurnId: current.activeTurnId || null };
    const out = await require('@/lib/remoteWork').withRemoteWorkTask(get().tasks[taskId] || { id: taskId }, remoteGuard =>
      withLocalProjectHandoff((extra, guard) => coworkerApi.resumeTask(taskId, message, fileIds, { ...options, ...extra }, { ...guard, ...remoteGuard })));
    if (!out.routing) get().addTask({ id: taskId, status: out.status || 'running', activeTurnId: out.activeTurnId || null,
      ...(out.stopControl ? { cancellationPending: out.cancellationPending === true } : {}) });
    return out;
  },

  // Follow-up turn on an idle thread (composer input).
  sendMessage: async (taskId, message, fileIds, options) => {
    const current = get().tasks[taskId];
    if (current?.status === 'running') options = { ...options, expectedTurnId: current.activeTurnId || null };
    const out = await require('@/lib/remoteWork').withRemoteWorkTask(get().tasks[taskId] || { id: taskId }, remoteGuard =>
      withLocalProjectHandoff((extra, guard) => coworkerApi.sendThreadMessage(taskId, message, fileIds, { ...options, ...extra }, { ...guard, ...remoteGuard })));
    if (!out.routing) get().addTask({ id: taskId, status: out.status || 'running', activeTurnId: out.activeTurnId || (out.goalControl && out.status === 'running' ? current?.activeTurnId : null),
      ...(out.stopControl ? { cancellationPending: out.cancellationPending === true } : {}),
      ...(Object.hasOwn(out, 'goal') ? { goal: out.goal, goalEventSeq: out.goalEventSeq || 0 } : {}) });
    return out;
  },

  // The composer owns its own optimistic lifecycle. External controls such
  // as Video studio use this signal to wake the mounted chat subscription.
  notifyExternalFollowUp: (taskId) => {
    get().addTask({ id: taskId, localFollowUpVersion: (get().tasks[taskId]?.localFollowUpVersion || 0) + 1 });
  },

  // ── Checkpoints ──────────────────────────────────────────────────────
  checkpointsByTask: {},

  fetchCheckpoints: async (taskId, { force = false } = {}) => {
    if (!taskId) return [];
    if (!force && Object.prototype.hasOwnProperty.call(get().checkpointsByTask, taskId)) {
      return get().checkpointsByTask[taskId];
    }
    const inFlight = checkpointFetches.get(taskId);
    if (inFlight) {
      const current = await inFlight;
      if (!force) return current;
    }
    const request = coworkerApi.fetchCheckpoints(taskId)
      .then((data) => {
        const checkpoints = Array.isArray(data?.checkpoints) ? data.checkpoints : [];
        set((state) => ({
          checkpointsByTask: { ...state.checkpointsByTask, [taskId]: checkpoints },
        }));
        return checkpoints;
      })
      .catch(() => {
        set((state) => ({
          checkpointsByTask: { ...state.checkpointsByTask, [taskId]: [] },
        }));
        return [];
      })
      .finally(() => {
        if (checkpointFetches.get(taskId) === request) checkpointFetches.delete(taskId);
      });
    checkpointFetches.set(taskId, request);
    return request;
  },

  // Put the workspace back to a stable checkpoint (idle threads only), then
  // immediately ingest the restore events and refresh task/checkpoint state.
  restoreCheckpoint: async (taskId, target) => {
    const events = get().eventsByTask[taskId] || [];
    const since = events.reduce((max, event) => Math.max(max, Number(event?.seq) || 0), 0);
    const ref = target && typeof target === 'object'
      ? (target.sha ? { sha: target.sha } : { turn: target.turn })
      : { turn: target };
    const out = await coworkerApi.restoreCheckpoint(taskId, ref);
    if (Array.isArray(out?.artifacts)) {
      get().addTask({ id: taskId, artifacts: out.artifacts });
    }
    const snapshot = await coworkerApi.fetchTaskEvents(taskId, since).catch(() => null);
    if (snapshot?.events?.length) get().ingestTaskEvents(taskId, snapshot.events);
    await Promise.all([
      get().fetchTask(taskId),
      get().fetchCheckpoints(taskId, { force: true }),
    ]);
    return { ...out, snapshot };
  },

  // ── Scheduled threads ────────────────────────────────────────────────
  schedules: [],
  schedulesLoading: false,
  schedulesError: null,

  fetchSchedules: async () => {
    set({ schedulesLoading: true, schedulesError: null });
    try {
      const schedules = await coworkerApi.fetchSchedules();
      set({ schedules, schedulesLoading: false });
      return schedules;
    } catch (err) {
      set({ schedulesLoading: false, schedulesError: err?.message || 'Failed to load schedules' });
      return null;
    }
  },

  createSchedule: async (body) => {
    const created = await coworkerApi.createSchedule(body);
    await get().fetchSchedules();
    return created;
  },

  updateSchedule: async (scheduleId, body) => {
    const out = await coworkerApi.updateSchedule(scheduleId, body);
    await get().fetchSchedules();
    return out;
  },

  deleteSchedule: async (scheduleId) => {
    await coworkerApi.deleteSchedule(scheduleId);
    set((state) => ({ schedules: state.schedules.filter((s) => s.id !== scheduleId) }));
  },

  runScheduleNow: async (scheduleId) => {
    return coworkerApi.runScheduleNow(scheduleId);
  },
}));
